import React, { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription } from "@/components/ui/dialog";
import { 
  TrendingUp, 
  FileText, 
  Plus, 
  Zap,
  CheckCircle,
  Clock,
  Search,
  Target,
  ChevronUp,
  ChevronDown,
  Lightbulb,
  ArrowUp,
  BarChart3,
  Globe,
  Code,
  Settings,
  Sparkles,
  Image as ImageIcon,
  Upload,
  Palette,
  Type,
  Eye,
  Download,
  Copy,
  X,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import { EnhancedArticlePreview } from "../EnhancedArticlePreview";
import { FullScreenArticlePreview } from "../FullScreenArticlePreview";
import { IntegratedArticlePreview } from "../IntegratedArticlePreview";
import { OptimizedArticlePreview } from "../OptimizedArticlePreview";
import { AdvancedGenerationModal } from "../AdvancedGenerationModal";
import { LimitReachedCard } from "../LimitReachedCard";
import { useSubscription } from "@/hooks/useSubscription";
import type { User } from "@supabase/supabase-js";
import { LANGUAGES_SIMPLE, getLanguageFlag, getLanguageName, getLanguageFromCountry } from "@/lib/languages";

// Curated list of commonly used languages for a simpler UI
const POPULAR_LANGUAGE_CODES = [
  // Major European Languages
  'en', // English
  'es', // Spanish
  'fr', // French
  'de', // German
  'it', // Italian
  'pt', // Portuguese
  'nl', // Dutch
  'pl', // Polish
  'sv', // Swedish
  'no', // Norwegian
  'da', // Danish
  'fi', // Finnish
  'cs', // Czech
  'hu', // Hungarian
  'ro', // Romanian
  'bg', // Bulgarian
  'hr', // Croatian
  'sk', // Slovak
  'sl', // Slovenian
  'tr', // Turkish
  'uk', // Ukrainian
  'el', // Greek
  'lt', // Lithuanian
  'lv', // Latvian
  'et', // Estonian
  'mk', // Macedonian
  'sq', // Albanian
  'sr', // Serbian
  'bs', // Bosnian
  'me', // Montenegrin
  'ca', // Catalan
  'eu', // Basque
  'gl', // Galician
  'cy', // Welsh
  'ga', // Irish
  'gd', // Scottish Gaelic
  'mt', // Maltese
  'is', // Icelandic
  'fo', // Faroese
  'lb', // Luxembourgish
  
  // Asian Languages
  'ja', // Japanese
  'ko', // Korean
  'zh', // Chinese (Simplified)
  'zh-tw', // Chinese (Traditional)
  'hi', // Hindi
  'bn', // Bengali
  'ur', // Urdu
  'ta', // Tamil
  'te', // Telugu
  'ml', // Malayalam
  'kn', // Kannada
  'gu', // Gujarati
  'pa', // Punjabi
  'th', // Thai
  'vi', // Vietnamese
  'id', // Indonesian
  'ms', // Malay
  'tl', // Filipino
  'my', // Burmese
  'km', // Khmer
  'lo', // Lao
  
  // Middle Eastern & African Languages
  'ar', // Arabic
  'fa', // Persian
  'he', // Hebrew
  'am', // Amharic
  'sw', // Swahili
  'ha', // Hausa
  'yo', // Yoruba
  'ig', // Igbo
  'zu', // Zulu
  'af', // Afrikaans
  'xh', // Xhosa
  
  // Americas Regional Variants
  'pt-br', // Portuguese (Brazil)
  'es-mx', // Spanish (Mexico)
  'es-ar', // Spanish (Argentina)
  'fr-ca'  // French (Canada)
];

interface Website {
  id: string;
  domain: string;
  analysis_status: string | null;
  analysis_started_at: string | null;
  analysis_completed_at: string | null;
  detected_niche: string | null;
  title: string;
  favicon_url: string;
}

interface ContentCard {
  id: string;
  searchVolume: number;
  title: string;
  contentScore: number;
  wordCount: string;
  difficulty: string;
  difficultyLevel: 'low' | 'medium' | 'high';
  priority: string;
  priorityLevel: 'low' | 'medium' | 'high';
  searchIntent: string;
  primaryKeyword: string;
  isGenerating?: boolean;
}

interface TopicCluster {
  id: string;
  name: string;
  description: string;
  articles: ContentCard[];
  isExpanded: boolean;
  relevance_score?: number;
}

interface MyContent2TabProps {
  user: User;
  website: Website;
  onShowArticlePreview?: (article: any) => void;
  isActive?: boolean; // Keep for compatibility but not used
}

interface ArticleGenerationSettings {
  // Writing Preferences
  language: string;
  writing_style: string;
  custom_writing_style?: string;
  brand_voice?: string;
  word_count_target: number;
  
  // Image Preferences
  image_style: string;
  custom_image_style?: string;
  include_images: boolean;
  image_count: number;
  
  // Content Structure
  include_internal_links: boolean;
  include_cta: boolean;
  
  // Branding
  brand_colors: string[];
  logo_url?: string;
  template_style: string;
  custom_template_css?: string;
}

interface GenerationJob {
  id: string;
  status: string;
  progress: number;
  current_step: string;
  error_message?: string;
}

export const MyContent2Tab: React.FC<MyContent2TabProps> = ({ 
  user, 
  website, 
  onShowArticlePreview,
  isActive = true 
}) => {
  const { checkBenefitLimit, subscription, benefits } = useSubscription(user);
  const [loading, setLoading] = useState(false); // Start with false - only show loading if we have no data
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisStartTime, setAnalysisStartTime] = useState<Date | null>(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [topicClusters, setTopicClusters] = useState<TopicCluster[]>([]);
  const [generatingArticles, setGeneratingArticles] = useState<Set<string>>(new Set());
  const [generatedArticles, setGeneratedArticles] = useState<Set<string>>(new Set());
  const [countdown, setCountdown] = useState(0);
  const [activeTab, setActiveTab] = useState("content-overview");
  
  // Enhanced generation system state
  const [showSettings, setShowSettings] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewArticleId, setPreviewArticleId] = useState<string | null>(null);
  const [showIntegratedPreview, setShowIntegratedPreview] = useState(false);
  const [currentJob, setCurrentJob] = useState<GenerationJob | null>(null);
  const [userSettings, setUserSettings] = useState<ArticleGenerationSettings>({
    // Writing Preferences
    language: 'en',
    writing_style: 'professional',
    brand_voice: 'professional',
    word_count_target: 2500, // Auto-calculated, not user-editable
    
    // Image Preferences
    image_style: 'realistic',
    include_images: true,
    image_count: 3,
    
    // Content Structure
    include_internal_links: true,
    include_cta: true,
    
    // Branding
    brand_colors: ['#3B82F6', '#1E40AF'],
    template_style: 'modern'
  });

  // Advanced Generation Modal
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<ContentCard | null>(null);
  const [selectedCluster, setSelectedCluster] = useState<TopicCluster | null>(null);
  const [modalAutoStart, setModalAutoStart] = useState(false); // Track if modal should auto-start generation
  
  // Ref to store loadAnalysisData function to avoid dependency issues in polling effect
  const loadAnalysisDataRef = useRef<(() => Promise<void>) | null>(null);
  const analysisStartedRef = useRef<boolean>(false);

  // Load real analysis data
  useEffect(() => {
    if (website?.id && isActive) {
      // Only show loading spinner if we don't have any data yet
      // Since component stays mounted (forceMount), state persists across tab switches
      // So we only show loading on the very first load when there's no data
      const hasData = topicClusters.length > 0;
      if (!hasData) {
        setLoading(true);
      }
      
      // Always refresh data in the background, but don't show loading if we already have data
      loadAnalysisData();
      loadUserSettings();
      loadGeneratedArticles();
    }
  }, [website?.id, isActive]);

  // Poll for analysis completion when analyzing is true
  useEffect(() => {
    if (!analyzing || !website?.id || !isActive || !analysisStartTime) return;

    console.log('[MyContent2Tab] Starting to poll for analysis completion...');
    
    let isPolling = true;
    
    const pollAnalysisStatus = async () => {
      if (!isPolling || !website?.id) return;
      
      try {
        // Only check for analyses created after we started this analysis
        const startTimeISO = analysisStartTime.toISOString();
        
        // Check if analysis has completed (only ones created after we started)
        const { data: completedAnalysis } = await supabase
          .from('website_analysis')
          .select('id, status, completed_at, created_at')
          .eq('website_id', website.id)
          .eq('status', 'completed')
          .gte('created_at', startTimeISO)
          .order('completed_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (completedAnalysis) {
          console.log('[MyContent2Tab] Analysis completed! Reloading data...');
          isPolling = false;
          setAnalyzing(false);
          analysisStartedRef.current = false; // Reset for next time
          setAnalysisStartTime(null);
          // Reload the analysis data to show results
          if (loadAnalysisDataRef.current) {
            await loadAnalysisDataRef.current();
          }
          toast.success('Website analysis completed! Your content strategy is ready.');
          return;
        }

        // Also check if there's still a running analysis
        const { data: runningAnalysis } = await supabase
          .from('website_analysis')
          .select('id, status')
          .eq('website_id', website.id)
          .in('status', ['processing', 'queued', 'pending'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // Check website_analysis_jobs too
        const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
        const { data: runningJob } = await supabase
          .from('website_analysis_jobs')
          .select('id, status')
          .eq('website_id', website.id)
          .in('status', ['processing', 'queued', 'pending'])
          .gte('created_at', twoHoursAgo)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // If no running analysis or job, stop polling and reload
        if (!runningAnalysis && !runningJob) {
          console.log('[MyContent2Tab] No running analysis found, stopping poll...');
          isPolling = false;
          setAnalyzing(false);
          analysisStartedRef.current = false; // Reset for next time
          setAnalysisStartTime(null);
          // Reload data one more time to check for completion
          if (loadAnalysisDataRef.current) {
            loadAnalysisDataRef.current();
          }
        }
      } catch (error) {
        console.error('[MyContent2Tab] Error polling analysis status:', error);
      }
    };

    // Poll immediately, then every 3 seconds
    pollAnalysisStatus();
    const pollInterval = setInterval(pollAnalysisStatus, 3000);

    // Cleanup: stop polling after 30 minutes (analysis shouldn't take that long)
    const timeoutId = setTimeout(() => {
      console.log('[MyContent2Tab] Polling timeout reached, stopping...');
      isPolling = false;
      clearInterval(pollInterval);
      setAnalyzing(false);
      analysisStartedRef.current = false; // Reset for next time
      setAnalysisStartTime(null);
    }, 30 * 60 * 1000); // 30 minutes

    return () => {
      isPolling = false;
      clearInterval(pollInterval);
      clearTimeout(timeoutId);
    };
  }, [analyzing, website?.id, isActive, analysisStartTime]);

  // Load user settings
  const loadUserSettings = async () => {
    try {
      // First, get user's profile to check country
      const { data: profile } = await supabase
        .from('profiles')
        .select('country')
        .eq('user_id', user.id)
        .maybeSingle()

      const { data, error } = await supabase
        .from('article_generation_settings')
        .select('*')
        .eq('user_id', user.id)
        .eq('website_id', website.id)
        .maybeSingle()

      if (error) {
        logger.error('Error loading settings:', error)
        return
      }

      // Determine default language: use existing setting, or country-based default, or 'en'
      let defaultLanguage = 'en'
      if (data?.language) {
        // Use existing setting if available
        defaultLanguage = data.language
      } else if (profile?.country) {
        // Use country-based language if no existing setting
        defaultLanguage = getLanguageFromCountry(profile.country)
      }

      if (data) {
        // Map database settings to our interface
        setUserSettings({
          // Writing Preferences
          language: data.language || defaultLanguage,
          writing_style: data.writing_style || 'professional',
          custom_writing_style: data.custom_writing_style,
          brand_voice: data.brand_voice || 'professional',
          word_count_target: 2500, // Auto-calculated, not user-editable
          
          // Image Preferences
          image_style: data.image_style || 'realistic',
          custom_image_style: data.custom_image_style,
          include_images: data.include_images !== false, // default to true
          image_count: data.image_count !== null && data.image_count !== undefined ? data.image_count : 3, // Allow 0, only default if null/undefined
          
          // Content Structure
          include_internal_links: data.include_internal_links !== false, // default to true
          include_cta: data.include_cta !== false, // default to true
          
          // Branding
          brand_colors: Array.isArray(data.brand_colors) ? data.brand_colors : (data.brand_colors ? [data.brand_colors] : ['#3B82F6', '#1E40AF']),
          logo_url: data.logo_url,
          template_style: data.template_style || 'modern',
          custom_template_css: data.custom_template_css
        })
      } else {
        // No settings found, use defaults with country-based language
        setUserSettings(prev => ({
          ...prev,
          language: defaultLanguage
        }))
      }
    } catch (error) {
      logger.error('Error loading settings:', error)
    }
  }

  // Save user settings
  const saveUserSettings = async () => {
    try {
      // Prepare settings object - explicitly handle 0 values
      const settingsToSave: any = {
        user_id: user.id,
        website_id: website.id,
        // Writing Preferences
        language: userSettings.language,
        writing_style: userSettings.writing_style,
        custom_writing_style: userSettings.custom_writing_style || null,
        brand_voice: userSettings.brand_voice,
        word_count_target: 2500, // Auto-calculated, not user-editable
        
        // Image Preferences - explicitly save 0 if set to 0
        image_style: userSettings.image_style,
        custom_image_style: userSettings.custom_image_style || null,
        include_images: userSettings.include_images,
        image_count: userSettings.image_count, // Save as-is, including 0
        
        // Content Structure
        include_internal_links: userSettings.include_internal_links,
        include_cta: userSettings.include_cta,
        
        // Branding
        brand_colors: userSettings.brand_colors,
        logo_url: userSettings.logo_url || null,
        template_style: userSettings.template_style,
        custom_template_css: userSettings.custom_template_css || null,
        
        updated_at: new Date().toISOString()
      }
      
      console.log('💾 Saving settings:', {
        image_count: settingsToSave.image_count,
        image_style: settingsToSave.image_style,
        include_images: settingsToSave.include_images
      })
      
      const { error, data } = await supabase
        .from('article_generation_settings')
        .upsert(settingsToSave, {
          onConflict: 'user_id,website_id'
        })
        .select()
        .single()

      if (error) throw error
      
      console.log('✅ Settings saved:', {
        image_count: data?.image_count,
        image_style: data?.image_style
      })
      
      // Reload settings after save to ensure they're persisted
      await loadUserSettings()
      
      toast.success('Settings saved successfully!')
    } catch (error: any) {
      console.error('Error saving settings:', error)
      toast.error(`Failed to save settings: ${error?.message || 'Unknown error'}`)
    }
  }

  // Load generated articles to check which ones are already generated
  const loadGeneratedArticles = async () => {
    try {
      const { data, error } = await supabase
        .from('generated_articles')
        .select('id, article_id, generation_status')
        .eq('website_id', website.id)
        .eq('generation_status', 'completed')

      if (error) throw error

      const generatedIds = new Set(data?.map(article => article.article_id) || [])
      setGeneratedArticles(generatedIds)
    } catch (error) {
      logger.error('Error loading generated articles:', error)
    }
  }

  const loadAnalysisData = async () => {
    if (!website?.id) return;
    
    // Only show loading if we don't have any data yet
    // This way, if data is already loaded, we refresh silently in the background
    const hasData = topicClusters.length > 0;
    if (!hasData) {
      setLoading(true);
    }
    
    try {
      // First check if there are any completed analyses - if yes, don't show analyzing message
      const { data: completedAnalyses } = await supabase
        .from('website_analysis')
        .select('id')
        .eq('website_id', website.id)
        .eq('status', 'completed')
        .limit(1)
        .maybeSingle();

      // Only show analyzing message if there are NO completed analyses AND there's a running one
      if (!completedAnalyses) {
        // Check if there's a running analysis (check both website_analysis and website_analysis_jobs)
        const { data: runningAnalysis } = await supabase
          .from('website_analysis')
          .select('id, status, created_at')
          .eq('website_id', website.id)
          .in('status', ['processing', 'queued', 'pending'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // Also check website_analysis_jobs - but only recent ones (within last 2 hours)
        const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
        const { data: runningJob } = await supabase
          .from('website_analysis_jobs')
          .select('id, status, created_at')
          .eq('website_id', website.id)
          .in('status', ['processing', 'queued', 'pending'])
          .gte('created_at', twoHoursAgo)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (runningAnalysis || runningJob) {
          setAnalyzing(true);
          setLoading(false);
          return;
        }
      }

      // Load ALL completed analyses for this website
      const { data: allAnalyses, error } = await supabase
        .from('website_analysis')
        .select(`
          id,
          status,
          completed_at,
          firecrawl_data,
          analysis_data
        `)
        .eq('website_id', website.id)
        .eq('status', 'completed')
        .order('created_at', { ascending: false });

      // Load topic clusters separately for all analyses
      let allClusters: any[] = [];
      if (allAnalyses && allAnalyses.length > 0) {
        const analysisIds = allAnalyses.map(a => a.id);
        // Load topic clusters
        const { data: clusters, error: clustersError } = await supabase
          .from('topic_clusters')
          .select('*')
          .in('analysis_id', analysisIds);
        
        if (clustersError) {
          console.error('Error loading clusters:', clustersError);
        } else {
          allClusters = clusters || [];
          
          // Load articles separately for all clusters
          if (allClusters.length > 0) {
            const clusterIds = allClusters.map(c => c.id);
            
            const { data: articles, error: articlesError } = await supabase
              .from('articles')
              .select('*')
              .in('topic_cluster_id', clusterIds);
            
            if (!articlesError && articles) {
              // Attach articles to their clusters
              allClusters = allClusters.map(cluster => ({
                ...cluster,
                articles: articles.filter(article => article.topic_cluster_id === cluster.id)
              }));
              // Articles loaded successfully
            } else {
              logger.error('Articles failed to load:', articlesError);
            }
          }
        }
      }

      if (error) {
        console.error('Error loading analysis data:', error);
        // Initialize with empty clusters if no data
        setTopicClusters([]);
        setLoading(false);
        return;
      }

      if (allClusters && allClusters.length > 0) {
        
        // Process topic clusters
        const processedClusters: TopicCluster[] = allClusters.map((cluster: any) => ({
              id: cluster.id,
              name: cluster.cluster_name,
              description: cluster.cluster_description,
              relevance_score: cluster.relevance_score,
              isExpanded: false,
              articles: (cluster.articles || []).map((article: any) => ({
                  id: article.id,
                  searchVolume: article.estimated_monthly_searches || 0,
                  title: article.title || "",
                  contentScore: article.seo_score || 0,
                  estimated_word_count: article.estimated_word_count || 2000,
                  difficulty: article.ranking_difficulty || "Medium",
                  difficultyLevel: article.ranking_difficulty?.toLowerCase() || "medium",
                  priority: article.llm_optimization_score > 85 ? "high" : article.llm_optimization_score > 70 ? "medium" : "low",
                  priorityLevel: article.llm_optimization_score > 85 ? "high" : article.llm_optimization_score > 70 ? "medium" : "low",
                  searchIntent: article.search_intent || "Informational",
                  primaryKeyword: article.primary_keyword || "",
                  isGenerating: false
                }))
            }));
        
        setTopicClusters(processedClusters);
      } else {
        // No analysis data - show empty state
        setTopicClusters([]);
      }
    } catch (error) {
      console.error('Error loading analysis data:', error);
      setTopicClusters([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Store loadAnalysisData in ref so polling effect can access it
  useEffect(() => {
    loadAnalysisDataRef.current = loadAnalysisData;
  }, [website?.id]); // Update ref when website changes

  // Reset analysis ref and state when website changes or component unmounts
  useEffect(() => {
    return () => {
      // Cleanup: reset ref and state when component unmounts or website changes
      analysisStartedRef.current = false;
      setAnalyzing(false);
    };
  }, [website?.id]);

  // Reset analysis state when modal closes
  useEffect(() => {
    if (!showAnalysisModal) {
      // Reset both ref and state when modal closes
      analysisStartedRef.current = false;
      setAnalyzing(false);
      setAnalysisStartTime(null);
    } else {
      // When modal opens, reset to allow new analysis
      analysisStartedRef.current = false;
      setAnalyzing(false);
    }
  }, [showAnalysisModal]);

  const startAnalysis = async () => {
    // CRITICAL: Set ref IMMEDIATELY to prevent race conditions from multiple clicks
    // This must happen BEFORE any async operations or state updates
    if (analysisStartedRef.current) {
      console.warn('⚠️ Analysis already started, ignoring duplicate call');
      return;
    }
    analysisStartedRef.current = true;
    
    // Prevent duplicate calls with multiple guards
    console.log('🔍 startAnalysis called:', {
      analyzing,
      analysisStartedRef: analysisStartedRef.current,
      websiteId: website?.id
    });
    
    // Additional check for state (though ref should catch it first)
    if (analyzing) {
      console.warn('⚠️ Analysis already in progress, ignoring duplicate call');
      analysisStartedRef.current = false; // Reset since we're returning early
      return;
    }

    if (!website?.id || !website?.domain) {
      analysisStartedRef.current = false; // Reset on validation error
      toast.error('Website information not available');
      return;
    }

    setAnalyzing(true);
    setAnalysisStartTime(new Date());
    try {
      const { data, error } = await supabase.functions.invoke('enhanced-firecrawl-gemini-v2', {
        body: {
          website_url: `https://${website.domain}`,
          user_id: user.id,
          website_id: website.id
        }
      });

      if (error) {
        // Parse error message - Supabase functions return errors in data.error when status is non-200
        let errorMessage = 'Failed to start analysis. Please try again.';
        
        if (data?.error) {
          errorMessage = data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Handle 409 conflict (duplicate analysis) with a user-friendly message
        if (errorMessage.includes('already running') || errorMessage.includes('409')) {
          toast.error('An analysis is already running for this website. Please wait for it to complete.');
        } else {
          toast.error(errorMessage);
        }
        
        setAnalyzing(false);
        analysisStartedRef.current = false; // Reset for next time
        setAnalysisStartTime(null);
        setShowAnalysisModal(false);
        return;
      }

      if (!data?.success) {
        const errorMessage = data?.error || 'Failed to start analysis';
        toast.error(errorMessage);
        setAnalyzing(false);
        analysisStartedRef.current = false; // Reset for next time
        setAnalysisStartTime(null);
        setShowAnalysisModal(false);
        return;
      }
      
      // Ref is already set at the start of the function, no need to set again
      
      // Show modal instead of toast
      setShowAnalysisModal(true);
      
      // Start polling for completion with backup checker
      const pollStartTime = analysisStartTime || new Date();
      const pollForCompletion = async () => {
        let pollCount = 0;
        const maxPolls = 30; // 5 minutes at 10-second intervals
        
        const checkInterval = setInterval(async () => {
          pollCount++;
          
          // Only check for analyses created after we started this analysis
          const startTimeISO = pollStartTime.toISOString();
          
          const { data: status } = await supabase
            .from('website_analysis')
            .select('id, status, completed_at, created_at')
            .eq('website_id', website.id)
            .gte('created_at', startTimeISO)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (status?.status === 'completed') {
            clearInterval(checkInterval);
            setAnalyzing(false);
            analysisStartedRef.current = false; // Reset for next time
            setAnalysisStartTime(null);
            setShowAnalysisModal(false);
            toast.success('Analysis completed! Loading your content strategy...');
            await loadAnalysisData();
            return;
          } else if (status?.status === 'failed') {
            clearInterval(checkInterval);
            setAnalyzing(false);
            analysisStartedRef.current = false; // Reset for next time
            setAnalysisStartTime(null);
            setShowAnalysisModal(false);
            toast.error('Analysis failed. Please try again.');
            return;
          }

          // After 2 minutes, start using backup checker
          if (pollCount >= 12) {
            try {
              const { data: backupCheck } = await supabase.functions.invoke('check-openrouter-completion', {
                body: {
                  analysis_id: status?.id,
                  website_id: website.id
                }
              });

              if (backupCheck?.status === 'completed') {
                clearInterval(checkInterval);
                setAnalyzing(false);
                analysisStartedRef.current = false; // Reset for next time
                setAnalysisStartTime(null);
                toast.success('Analysis completed via backup checker! Loading results...');
                await loadAnalysisData();
                return;
              } else if (backupCheck?.status === 'completed_externally') {
                clearInterval(checkInterval);
                setAnalyzing(false);
                analysisStartedRef.current = false; // Reset for next time
                setAnalysisStartTime(null);
                toast.info('Analysis completed externally. Please click "Start Analysis" again to retrieve results.');
                return;
              }
            } catch (backupError) {
              console.error('Backup checker error:', backupError);
            }
          }

          // Stop polling after 5 minutes
          if (pollCount >= maxPolls) {
            clearInterval(checkInterval);
            setAnalyzing(false);
            analysisStartedRef.current = false; // Reset for next time
            setAnalysisStartTime(null);
            toast.info('Analysis is taking longer than expected. You can check back in a few minutes or try starting a new analysis.');
          }
        }, 10000); // Check every 10 seconds
      };

      pollForCompletion();
      
    } catch (error: any) {
      console.error('Analysis error:', error);
      
      // Extract error message from various error formats
      let errorMessage = 'Failed to start analysis. Please try again.';
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.error) {
        errorMessage = error.error;
      }
      
      // Handle specific error cases
      if (errorMessage.includes('already running') || errorMessage.includes('409')) {
        toast.error('An analysis is already running. Please wait for it to complete.');
      } else {
        toast.error(errorMessage);
      }
      
      setAnalyzing(false);
      analysisStartedRef.current = false; // Reset for next time
      setAnalysisStartTime(null);
    }
  };

  // Fallback initialization for empty state
  useEffect(() => {
    if (!loading && topicClusters.length === 0) {
      // Show empty state instead of hardcoded data
    }
  }, [loading, topicClusters.length]);

  // Show analyzing screen if analysis is running (check this BEFORE loading check)
  if (analyzing && !loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Analyzing Your Website
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center py-8">
              <div className="animate-pulse text-6xl mb-4">🧠</div>
              <h3 className="text-lg font-semibold mb-2">We are analyzing your website...</h3>
              <p className="text-muted-foreground mb-4">
                We're examining your site structure, content, competitors, and creating topic clusters with article ideas.
                This process may take a few minutes depending on your website size.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span>Analysis in progress...</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading your content strategy...</p>
        </div>
      </div>
    );
  }

  // If no analysis data, show start analysis prompt  
  if (topicClusters.length === 0) {
    return (
      <div className="space-y-6">
        <Alert>
          <Lightbulb className="h-4 w-4" />
          <AlertDescription>
            No content analysis found. Start your first analysis to generate topic clusters and article ideas based on your website.
          </AlertDescription>
        </Alert>
        
        <Card>
          <CardHeader>
            <CardTitle>Generate Content Strategy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Analyze your website with AI to generate 10 topic clusters and 60 article ideas optimized for search and engagement.
            </p>
            <Button onClick={startAnalysis} disabled={analyzing} className="w-full">
              {analyzing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Analyzing Website...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  Start Analysis
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Real data loading is handled in the useEffect above

  // Real analysis function is defined above

  const toggleCluster = (clusterId: string) => {
    setTopicClusters(prev => 
      prev.map(cluster => 
        cluster.id === clusterId 
          ? { ...cluster, isExpanded: !cluster.isExpanded }
          : cluster
      )
    );
  };

  // Poll for job progress
  const pollJobProgress = async (jobId: string, articleId?: string) => {
    try {
      const { data, error } = await supabase
        .from('article_generation_jobs')
        .select('*')
        .eq('id', jobId)
        .single()

      if (error) throw error

      setCurrentJob(data)

      if (data.status === 'completed' && data.generated_article_id) {
        if (articleId) {
          setGeneratingArticles(prev => {
            const newSet = new Set(prev);
            newSet.delete(articleId);
            return newSet;
          });
        }
        setCurrentJob(null)
        toast.success('Article generated successfully!')
        setPreviewArticleId(data.generated_article_id)
        setShowIntegratedPreview(true)
        // Refresh the generated articles list
        loadGeneratedArticles()
      } else if (data.status === 'failed') {
        if (articleId) {
          setGeneratingArticles(prev => {
            const newSet = new Set(prev);
            newSet.delete(articleId);
            return newSet;
          });
        }
        setCurrentJob(null)
        toast.error(data.error_message || 'Article generation failed')
      } else if (data.status === 'processing') {
        // Continue polling
        setTimeout(() => pollJobProgress(jobId, articleId), 2000)
      }
    } catch (error) {
      console.error('Error polling job progress:', error)
      if (articleId) {
        setGeneratingArticles(prev => {
          const newSet = new Set(prev);
          newSet.delete(articleId);
          return newSet;
        });
      }
      setCurrentJob(null)
      toast.error('Failed to track generation progress')
    }
  }

  const generateArticle = async (article: ContentCard, cluster: TopicCluster) => {
    // Prevent duplicate calls - check if already generating this article
    if (generatingArticles.has(article.id)) {
      console.warn('⚠️ Article generation already in progress for:', article.id);
      return;
    }
    
    try {
      // Check limit before allowing generation
      const limitCheck = checkBenefitLimit('article_generation');
      if (!limitCheck.allowed) {
        toast.error(`Article generation limit reached. You have used all ${limitCheck.remaining === 0 ? 'your' : limitCheck.remaining} available articles this month.`);
        return;
      }

      // Check if article already exists - get the most recent one if multiple exist
      const { data: existingArticle } = await supabase
        .from('generated_articles')
        .select('*')
        .eq('article_id', article.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existingArticle) {
        setPreviewArticleId(existingArticle.id);
        setShowIntegratedPreview(true);
        toast.success('Article already generated!');
        loadGeneratedArticles();
        return;
      }

      // Show modal for generation with smart defaults (no settings page)
      setSelectedArticle(article);
      setSelectedCluster(cluster);
      setModalAutoStart(true); // Enable auto-start
      setShowAdvancedModal(true);
      setGeneratingArticles(prev => new Set(prev).add(article.id));

    } catch (error) {
      console.error('❌ Article check failed:', error);
      toast.error(error.message || 'Failed to check article status');
    }
  };

  const openAdvancedSettings = (article: ContentCard, cluster: TopicCluster) => {
    setSelectedArticle(article);
    setSelectedCluster(cluster);
    setModalAutoStart(false); // Don't auto-start, show settings first
    setShowAdvancedModal(true);
  };

  const handleGenerationSuccess = (generatedArticleId: string) => {
    // Clear generating state when generation succeeds
    if (selectedArticle) {
      setGeneratingArticles(prev => {
        const newSet = new Set(prev);
        newSet.delete(selectedArticle.id);
        return newSet;
      });
    }
    setPreviewArticleId(generatedArticleId);
    setShowIntegratedPreview(true);
    loadGeneratedArticles();
  };

  // View already generated article
  const viewGeneratedArticle = async (articleId: string) => {
    try {
      // Find the generated article by article_id - get the most recent one if multiple exist
      const { data, error } = await supabase
        .from('generated_articles')
        .select('id, title, generation_status')
        .eq('article_id', articleId)
        .eq('website_id', website.id)
        .eq('generation_status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) throw error

      if (data) {
        setPreviewArticleId(data.id)
        setShowIntegratedPreview(true)
        // Opening article silently
      } else {
        // Article not found - silent
      }
    } catch (error) {
      console.error('Error viewing generated article:', error)
      // Failed to open - silent
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty?.toLowerCase()) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-amber-200 text-amber-900';
      case 'high': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'low': return 'bg-blue-100 text-blue-800';
      case 'medium': return 'bg-amber-200 text-amber-900';
      case 'high': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getIntentIcon = (intent: string) => {
    switch (intent?.toLowerCase()) {
      case 'informational': return <FileText className="h-4 w-4" />;
      case 'commercial': return <TrendingUp className="h-4 w-4" />;
      case 'transactional': return <Target className="h-4 w-4" />;
      default: return <Search className="h-4 w-4" />;
    }
  };

  // Remove loading state since data is pre-initialized
  // if (loading) {
  //   return (
  //     <div className="flex items-center justify-center p-8">
  //       <div className="text-center">
  //         <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
  //         <p className="text-muted-foreground">Loading your NFC content strategy...</p>
  //       </div>
  //     </div>
  //   );
  // }

  // Show optimized preview if active
  if (showIntegratedPreview && previewArticleId) {
    return (
      <OptimizedArticlePreview
        articleId={previewArticleId}
        onClose={() => {
          setShowIntegratedPreview(false)
          setPreviewArticleId(null)
        }}
        onBack={() => {
          setShowIntegratedPreview(false)
          setPreviewArticleId(null)
        }}
      />
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Content Overview - Main Content Tab */}
      <div className="space-y-4">
          {/* Enhanced Generation Settings & Progress */}
          <div className="bg-gradient-to-br from-primary/8 via-primary/5 to-primary/12 border border-primary/20 rounded-2xl p-6">
            {/* Single Line with Article Settings and All Settings */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-8">
                <h3 className="text-lg font-semibold text-foreground whitespace-nowrap">Article Settings</h3>
                
                <div className="flex items-center gap-8">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{getLanguageFlag(userSettings.language)}</span>
                    <div>
                      <div className="text-xs text-muted-foreground">Language</div>
                      <div className="text-sm font-medium text-foreground">{getLanguageName(userSettings.language)}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Writing Style</div>
                      <div className="text-sm font-medium text-foreground capitalize">{userSettings.writing_style || 'Professional'}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Image Style</div>
                      <div className="text-sm font-medium text-foreground capitalize">{userSettings.image_style || 'Realistic'}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Images</div>
                      <div className="text-sm font-medium text-foreground">{userSettings.image_count ?? 3} Images</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <Button
                onClick={() => setShowSettings(true)}
                variant="outline"
                size="sm"
                className="h-9 px-4 border-primary/20 text-primary hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-all duration-200"
              >
                <Settings className="h-4 w-4 mr-2" />
                <span className="text-sm">Settings</span>
              </Button>
            </div>

            {/* Progress Tracking */}
            {currentJob && (
              <div className="bg-card/80 backdrop-blur-sm rounded-lg p-4 border border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Generation Progress</span>
                  <span className="text-sm text-muted-foreground">{currentJob.progress || 0}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 mb-2">
                  <div 
                    className="bg-gradient-to-r from-primary to-primary/80 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${currentJob.progress || 0}%` }}
                  />
                </div>
                {currentJob.current_step && (
                  <p className="text-xs text-muted-foreground">{currentJob.current_step}</p>
                )}
              </div>
            )}

            {/* Simple Settings Popup */}
            {showSettings && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-card rounded-2xl p-6 w-full max-w-md border border-border shadow-2xl">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-lg font-semibold text-foreground">Article Settings</h4>
                    <Button
                      onClick={() => setShowSettings(false)}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-foreground">Language</Label>
                      <Select
                        value={userSettings.language}
                        onValueChange={(value) => setUserSettings(prev => ({ ...prev, language: value }))}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LANGUAGES_SIMPLE
                            .filter(l => POPULAR_LANGUAGE_CODES.includes(l.value))
                            .filter(l => l.value !== 'ru' && l.value !== 'be')
                            .map(lang => (
                            <SelectItem key={lang.value} value={lang.value}>
                              <span className="mr-2">{lang.flag}</span>
                              {lang.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-foreground">Writing Style</Label>
                      <Select
                        value={userSettings.writing_style}
                        onValueChange={(value) => setUserSettings(prev => ({ ...prev, writing_style: value }))}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="casual">Casual</SelectItem>
                          <SelectItem value="friendly">Friendly</SelectItem>
                          <SelectItem value="authoritative">Authoritative</SelectItem>
                          <SelectItem value="conversational">Conversational</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-foreground">Image Style</Label>
                      <Select
                        value={userSettings.image_style}
                        onValueChange={(value) => setUserSettings(prev => ({ ...prev, image_style: value }))}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="realistic">Realistic</SelectItem>
                          <SelectItem value="cartoonish">Cartoonish</SelectItem>
                          <SelectItem value="minimalist">Minimalist</SelectItem>
                          <SelectItem value="vintage">Vintage</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-foreground">Number of Images</Label>
                      <Select
                        value={userSettings.image_count.toString()}
                        onValueChange={(value) => setUserSettings(prev => ({ 
                          ...prev, 
                          image_count: parseInt(value) 
                        }))}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">0 Images</SelectItem>
                          <SelectItem value="3">3 Images</SelectItem>
                          <SelectItem value="4">4 Images</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button
                      onClick={() => setShowSettings(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        saveUserSettings();
                        setShowSettings(false);
                      }}
                      className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

      {/* Topic Clusters with Beautiful Card Design */}
      <div className="space-y-8">
        {topicClusters.map((cluster) => (
          <div key={cluster.id} className="space-y-6">
            {/* Beautiful Cluster Header Card */}
            <div className="bg-background/95 backdrop-blur-md border border-border/40 shadow-[0_2px_12px_rgba(0,0,0,0.04),0_1px_4px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.06),0_2px_8px_rgba(0,0,0,0.03)] rounded-3xl transition-all duration-300 ease-out p-8">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {cluster.name}
                  </h3>
                  
                  {/* Metrics Row */}
                  <div className="flex items-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <ArrowUp className="h-4 w-4 text-primary" />
                      <span>{cluster.articles.reduce((sum, article) => sum + article.searchVolume, 0)} / month</span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <Target className="h-4 w-4 text-primary" />
                      <span>Difficulty: </span>
                      <span className="bg-muted text-muted-foreground px-2 py-0.5 rounded-full text-xs">
                        {(() => {
                          const difficulties = cluster.articles.map(article => {
                            switch(article.difficulty.toLowerCase()) {
                              case 'low': return 1;
                              case 'medium': return 2;
                              case 'high': return 3;
                              default: return 1;
                            }
                          });
                          const avgDifficulty = difficulties.reduce((sum, diff) => sum + diff, 0) / difficulties.length;
                          if (avgDifficulty <= 1.3) return 'Low';
                          if (avgDifficulty <= 2.3) return 'Medium';
                          return 'High';
                        })()}
                      </span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <FileText className="h-4 w-4 text-primary" />
                      <span>Articles: {cluster.articles.length}</span>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex items-center gap-3">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => toggleCluster(cluster.id)}
                    className="gap-2 text-gray-700 border-gray-300 hover:bg-gray-50"
                  >
                    {cluster.isExpanded ? (
                      <>
                        <ChevronUp className="h-4 w-4" />
                        Show Less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4" />
                        Show All {cluster.articles.length} Articles
                      </>
                    )}
                  </Button>
                  <Button 
                    size="sm"
                    className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl transition-all duration-300"
                  >
                    <Plus className="h-4 w-4" />
                    New Template
                  </Button>
                </div>
              </div>
            </div>

            {/* Article Grid - Professional Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(() => {
                // Sort articles: non-generated first, then generated
                const sortedArticles = [...cluster.articles].sort((a, b) => {
                  const aGenerated = generatedArticles.has(a.id);
                  const bGenerated = generatedArticles.has(b.id);
                  
                  // Non-generated articles come first
                  if (!aGenerated && bGenerated) return -1;
                  if (aGenerated && !bGenerated) return 1;
                  
                  // If both have same generation status, maintain original order
                  return 0;
                });
                
                
                // Take the appropriate number based on expansion state
                const articlesToShow = cluster.isExpanded ? sortedArticles : sortedArticles.slice(0, 3);
                
                return articlesToShow.map((article) => {
                const isGenerating = generatingArticles.has(article.id);
                const isGenerated = generatedArticles.has(article.id);
                
                return (
                  <div key={article.id} className="bg-background/95 backdrop-blur-md border border-border/40 shadow-[0_2px_12px_rgba(0,0,0,0.04),0_1px_4px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.06),0_2px_8px_rgba(0,0,0,0.03)] rounded-3xl transition-all duration-300 ease-out p-8 h-[420px] flex flex-col">
                    {/* Monthly Value */}
                    <div className="text-4xl font-bold text-gray-900 mb-4">
                      {article.searchVolume}
                      <span className="text-base text-gray-400 font-normal ml-2">/ month</span>
                    </div>

                    {/* Title */}
                    <h3 className="text-base font-bold text-gray-900 mb-6 leading-tight flex-shrink-0">
                      {article.title}
                    </h3>

                    {/* Content Score */}
                    <div className="mb-6 flex-shrink-0">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-gray-500">Content Score</span>
                        <span className="text-sm font-semibold text-green-600">{article.contentScore}/100</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="bg-green-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${article.contentScore}%` }}></div>
                      </div>
                    </div>

                    {/* Details Section - Horizontal Layout */}
                    <div className="grid grid-cols-3 gap-6 mb-8 flex-shrink-0">
                      <div className="text-center space-y-2">
                        <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Words</div>
                        <div className="inline-flex rounded-full px-3 py-1.5 text-xs font-semibold bg-muted text-muted-foreground">
                          {article.estimated_word_count ? `~${article.estimated_word_count.toLocaleString()}` : '~2,000'}
                        </div>
                      </div>
                      <div className="text-center space-y-2">
                        <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Difficulty</div>
                        <div className={`inline-flex rounded-full px-3 py-1.5 text-xs font-semibold ${getDifficultyColor(article.difficulty)}`}>
                          {article.difficulty.toLowerCase()}
                        </div>
                      </div>
                      <div className="text-center space-y-2">
                        <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Priority</div>
                        <div className={`inline-flex rounded-full px-3 py-1.5 text-xs font-semibold ${getPriorityColor(article.priority)}`}>
                          {article.priority}
                        </div>
                      </div>
                    </div>

                    {/* Spacer to push button to bottom */}
                    <div className="flex-1"></div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => isGenerated ? viewGeneratedArticle(article.id) : generateArticle(article, cluster)}
                        disabled={isGenerating}
                        variant={isGenerated ? "default" : "outline"}
                        className={`flex-1 text-sm font-medium py-3 rounded-xl transition-all duration-300 gap-2 relative overflow-hidden group ${
                          isGenerated 
                            ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                            : "border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary"
                        }`}
                      >
                        {isGenerating ? (
                          <div className="flex items-center gap-2 relative z-10">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                            <span>Generating...</span>
                          </div>
                        ) : (
                          <>
                            {isGenerated ? (
                              <>
                                <Eye className="h-4 w-4 relative z-10" />
                                <span className="relative z-10">View</span>
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-4 w-4 relative z-10" />
                                <span className="relative z-10">Generate</span>
                              </>
                            )}
                          </>
                        )}
                        {/* Beautiful shine effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
                      </Button>
                      
                    </div>
                  </div>
                );
                });
              })()}
            </div>

          </div>
        ))}
      </div>

      {/* Get More Topic Clusters Section */}
      {topicClusters.length > 0 && (
        <div className="mt-12">
          <Card className="bg-background/95 backdrop-blur-md border border-border/40 shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-3xl transition-all duration-300">
            <CardContent className="p-6">
              {(() => {
                const analysisLimit = checkBenefitLimit('analysis');
                const analysisBenefit = benefits.find(b => b.benefit_type === 'analysis');
                
                if (!analysisLimit.allowed && analysisLimit.remaining === 0) {
                  return (
                    <div className="flex items-center gap-4">
                      <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground">
                          Analysis limit reached ({analysisBenefit?.used_count || 0}/{analysisBenefit?.benefit_limit || 1}).
                          {subscription?.current_period_end && (
                            <span className="ml-1">
                              Resets {new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <Sparkles className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Need more topic clusters?</p>
                        {analysisLimit.remaining > 0 && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {analysisLimit.remaining} analysis{analysisLimit.remaining !== 1 ? 'es' : ''} remaining
                          </p>
                        )}
                      </div>
                    </div>
                    <Button 
                      onClick={startAnalysis} 
                      disabled={analyzing}
                      className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl px-6"
                    >
                      {analyzing ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          <span>Analyzing...</span>
                        </>
                      ) : (
                        <>
                          <Zap className="h-4 w-4" />
                          <span>Run Analysis</span>
                        </>
                      )}
                    </Button>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}
      </div>

      {/* Full Screen Article Preview */}
      {showPreview && previewArticleId && (
        <FullScreenArticlePreview
          articleId={previewArticleId}
          onClose={() => {
            setShowPreview(false)
            setPreviewArticleId(null)
          }}
        />
      )}

      {/* Analysis Running Modal */}
      <Dialog open={showAnalysisModal} onOpenChange={(open) => {
        // Don't allow closing while analyzing
        if (!open && analyzing) return;
        setShowAnalysisModal(open);
        // Note: State reset is handled in useEffect above
      }}>
        <DialogContent className="sm:max-w-sm" hideCloseButton={analyzing}>
          <div className="flex flex-col items-center text-center space-y-4 py-2">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
            </div>
            <div className="space-y-1">
              <h3 className="text-base font-semibold">Analysis Started</h3>
              <DialogDescription className="text-sm">
                Your analysis is running in the background. This usually takes 2-3 minutes.
              </DialogDescription>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Advanced Generation Modal */}
      {showAdvancedModal && selectedArticle && (
        <AdvancedGenerationModal
          isOpen={showAdvancedModal}
          onClose={() => {
            // Clear generating state when modal closes
            if (selectedArticle) {
              setGeneratingArticles(prev => {
                const newSet = new Set(prev);
                newSet.delete(selectedArticle.id);
                return newSet;
              });
            }
            setShowAdvancedModal(false);
            setSelectedArticle(null);
            setSelectedCluster(null);
            setModalAutoStart(false);
          }}
          articleId={selectedArticle.id}
          articleTitle={selectedArticle.title}
          articleData={selectedArticle}
          analysisData={selectedCluster}
          userId={user.id}
          websiteId={website.id}
          userSettings={userSettings}
          onSuccess={handleGenerationSuccess}
          autoStart={modalAutoStart}
        />
      )}
    </div>
  );
};
